/**
 * TechniqueSearchStep Component
 *
 * Step for searching and selecting MITRE ATT&CK techniques.
 * Used as step 1 in the Technique wizard.
 *
 * Features:
 * - Two-column table with Category (tactic) and TTP Name columns
 * - Techniques appear once per tactic they belong to (full MITRE accuracy)
 * - Prepopulated with all MITRE techniques sorted by category then name
 * - Client-side filtering as user types (includes tactic name in search)
 * - Custom technique creation via "Add custom" option
 * - Input sanitization for security
 */

import { useCallback, useState } from 'react';

import {
  techniqueColumns,
  techniqueDefaultSort,
} from '@/sections/attacks/attackPaths/components/wizards/columns';
import {
  AddCustomOption,
  CurrentlyEditingDisplay,
  SearchEmptyState,
  SelectionTable,
  WizardSearchInput,
} from '@/sections/attacks/attackPaths/components/wizards/components';
import {
  type SearchableListItem,
  useSearchableList,
} from '@/sections/attacks/attackPaths/components/wizards/hooks';
import type { TechniqueWizardFormData } from '@/sections/attacks/attackPaths/components/wizards/types';
import {
  MAX_TECHNIQUE_ID_LENGTH,
  sanitizeInput,
} from '@/sections/attacks/attackPaths/components/wizards/types';

/**
 * Props for TechniqueSearchStep component
 */
interface TechniqueSearchStepProps {
  /** Current wizard form data */
  formData: TechniqueWizardFormData;
  /** Form data setter */
  setFormData: (
    updater:
      | TechniqueWizardFormData
      | ((prev: TechniqueWizardFormData) => TechniqueWizardFormData)
  ) => void;
  /**
   * Optional preselected technique key for edit mode.
   * When provided, ensures this technique is included in results
   * and shown in the "Currently Editing" display.
   */
  preselectedKey?: string;
}

/**
 * Technique search step for Technique wizard.
 *
 * Provides a search input with two-column SelectionTable for selecting
 * MITRE ATT&CK techniques or adding custom techniques.
 *
 * Columns:
 * - Category: MITRE tactic (e.g., "Reconnaissance", "Execution")
 * - TTP Name: Technique name with ID as metadata
 */
export function TechniqueSearchStep({
  formData,
  setFormData,
  preselectedKey,
}: TechniqueSearchStepProps) {
  const [searchValue, setSearchValue] = useState('');

  // Fetch and filter data
  const { items, isLoading } = useSearchableList({
    type: 'technique',
    searchTerm: searchValue,
    // Pass preselectedKey to ensure the selected technique is always in the list (for edit mode)
    preselectedKey,
  });

  /**
   * Handle search change
   */
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  /**
   * Handle technique selection from table
   * Note: item.key is uniqueKey (tactic-technique), item.metadata is the actual technique ID
   */
  const handleSelect = useCallback(
    (item: SearchableListItem) => {
      // Use metadata (technique ID) for storage, not uniqueKey
      const techniqueId = item.metadata ?? item.key;
      setFormData(prev => ({
        ...prev,
        techniqueId: sanitizeInput(techniqueId, MAX_TECHNIQUE_ID_LENGTH),
        techniqueName: item.name,
        customName: undefined,
      }));
      setSearchValue(''); // Clear search after selection
    },
    [setFormData]
  );

  /**
   * Handle custom technique entry
   */
  const handleAddCustom = useCallback(
    (term: string) => {
      setFormData(prev => ({
        ...prev,
        customName: term,
        techniqueId: '',
        techniqueName: '',
      }));
      setSearchValue('');
    },
    [setFormData]
  );

  // Find the selected item's uniqueKey based on stored techniqueId
  // Since a technique can appear in multiple tactics, we find any matching row
  const selectedKey = formData.customName
    ? null
    : (items.find(item => item.metadata === formData.techniqueId)?.key ?? null);

  // Dynamic row count: 8 rows in Add mode, 6 in Edit mode
  const isEditMode = Boolean(preselectedKey);
  const tableMaxRows = isEditMode ? 6 : 8;

  return (
    <div className="flex flex-col gap-4" data-testid="technique-search-step">
      {/* Currently Editing Display (Edit Mode Only) */}
      {preselectedKey && formData.techniqueId && !formData.customName && (
        <CurrentlyEditingDisplay
          name={formData.techniqueName || formData.techniqueId}
          metadata={formData.techniqueId}
          category={undefined}
          isCustom={false}
        />
      )}

      {/* Currently Selected Custom Technique */}
      {formData.customName && (
        <CurrentlyEditingDisplay name={formData.customName} isCustom={true} />
      )}

      <p
        id="technique-search-description"
        className="text-chariot-text-secondary text-sm"
      >
        Search for a MITRE ATT&CK technique by name or ID, or add a custom
        technique to include in the attack path.
      </p>

      {/* Search Input */}
      <WizardSearchInput
        value={searchValue}
        onChange={handleSearchChange}
        placeholder="Search by technique name or ID (e.g., T1059)..."
        ariaDescribedBy="technique-search-description"
        testId="technique-search-input"
        autoFocus
      />

      {/* Add Custom Option */}
      <AddCustomOption
        searchTerm={searchValue}
        onAddCustom={handleAddCustom}
        entityName="technique"
        testIdPrefix="technique-add-custom"
      />

      {/* Results Table or Empty State */}
      {items.length === 0 && searchValue.trim() ? (
        <SearchEmptyState
          searchTerm={searchValue}
          onClearSearch={() => setSearchValue('')}
          entityName="techniques"
          testIdPrefix="technique-search-empty"
        />
      ) : (
        <SelectionTable
          data={items}
          columns={techniqueColumns}
          selectedKey={selectedKey}
          onSelect={handleSelect}
          isLoading={isLoading}
          defaultSort={techniqueDefaultSort}
          maxRows={tableMaxRows}
          highlightSelected={!preselectedKey}
        />
      )}
    </div>
  );
}
